/**
 * SSEManager - Centralized SSE connection management
 * Handles SSE connection lifecycle and event dispatching to Redux store
 */

import { Store } from 'redux';

import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { sseConnectionClosed, sseConnectionError, sseEventReceived } from '../../store/closeout';
import { NullableString } from '../../util/types';
import GlobalService from '../Global.service';
import { LoggerService } from '../logger/Logger.service';

import { SSEEvent } from './sse-event-types';
import { CloudEvent } from './types';

type SSEEventHandler = (event: SSEEvent) => void;

const logger = LoggerService.getLogger();

export class SSEManager {
  private static instance: SSEManager | null = null;
  private cleanupFn: (() => void) | null = null;
  private isConnected: boolean = false;
  private isIntentionalDisconnect: boolean = false;
  private eventHandlers: Map<string, SSEEventHandler[]> = new Map();
  private store: Store | null = null;
  private currentAccountReferenceId: NullableString = null;
  /** The account the resource controller last asked for. */
  private desiredAccountReferenceId: NullableString = null;

  // eslint-disable-next-line no-useless-constructor
  private constructor() {
    // to use the existing instance or create a new one please call SSEManager.getInstance()
  }

  public static getInstance(): SSEManager {
    SSEManager.instance ??= new SSEManager();
    return SSEManager.instance;
  }

  /**
   * Set Redux store for dispatching actions
   * Should be called once during app initialization
   */
  public setStore(store: Store): void {
    this.store = store;
    logger.info('[SSEManager] Redux store initialized');

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('online', this.handleOnline);
    }
  }

  private handlePageHide = (): void => {
    this.disconnect();
  };

  private handleVisibilityChange = (): void => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!document.hidden && !this.isConnected) {
      logger.info('[SSEManager] Page became visible — reconnecting SSE if needed');
      this.reapplyDesired();
    }
  };

  private handleOnline = (): void => {
    if (!this.isConnected) {
      logger.info('[SSEManager] Network came back online — reconnecting SSE');
      this.reapplyDesired();
    }
  };

  /**
   * Connect to, or disconnect from, the account the flow has decided on.
   *
   * The manager no longer works out for itself whether it should be connected - the resource
   * controller (`store/account-flow/resources.ts`) owns that decision for all three connections,
   * so they cannot drift apart. `null` means "nothing should be connected".
   */
  public applyDesired(accountReferenceId: NullableString): void {
    this.desiredAccountReferenceId = accountReferenceId;

    if (!accountReferenceId) {
      this.disconnect();
      return;
    }

    if (this.isConnected && accountReferenceId !== this.currentAccountReferenceId) {
      this.disconnect();
    }

    if (!this.isConnected) {
      this.connect(accountReferenceId);
    }
  }

  /** Reconnects to whatever was last asked for, after the page or the network came back. */
  private reapplyDesired(): void {
    this.applyDesired(this.desiredAccountReferenceId);
  }

  /**
   * Start SSE connection for a specific account
   */
  public connect(accountReferenceId: string): void {
    if (this.isConnected) {
      logger.warn('[SSEManager] Already connected. Disconnect first before reconnecting.');
      return;
    }

    if (!accountReferenceId) {
      console.error('[SSEManager] Cannot connect: accountReferenceId is required');
      return;
    }

    this.currentAccountReferenceId = accountReferenceId;
    this.cleanupFn = GlobalService.closeoutService.streamAccountEvents(
      accountReferenceId,
      this.handleEvent.bind(this),
      this.handleError.bind(this),
      this.handleClose.bind(this),
    );

    this.isConnected = true;
  }

  /**
   * Disconnect SSE connection
   */
  public disconnect(): void {
    if (!this.isConnected) {
      return;
    }

    this.isIntentionalDisconnect = true;

    if (this.cleanupFn) {
      this.cleanupFn();
      this.cleanupFn = null;
    }

    this.isConnected = false;
  }

  /**
   * Check if SSE is currently connected
   */
  public getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Subscribe to specific event types
   */
  public on(eventType: string, handler: SSEEventHandler): () => void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }

    const handlers = this.eventHandlers.get(eventType)!;
    handlers.push(handler);

    // Return unsubscribe function
    return () => {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    };
  }

  /**
   * Handle incoming SSE events
   */
  private handleEvent(event: CloudEvent): void {
    console.warn('[SSEManager] Received event:', event.type, event);

    // Parse event data if it's a string
    let parsedEvent: SSEEvent = event as SSEEvent;

    if (typeof event.data === 'string') {
      try {
        parsedEvent = {
          ...event,
          data: JSON.parse(event.data),
        } as SSEEvent;
      } catch (error) {
        console.warn('[SSEManager] Event data is not JSON:', event.data);
      }
    }

    // Dispatch to Redux store if available
    if (this.store) {
      this.dispatchToStore(parsedEvent);
    }

    // Notify specific event handlers
    const handlers = this.eventHandlers.get(parsedEvent.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(parsedEvent);
        } catch (error) {
          console.error('[SSEManager] Error in event handler:', error);
        }
      });
    }

    // Notify wildcard handlers (listening to all events)
    const wildcardHandlers = this.eventHandlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach(handler => {
        try {
          handler(parsedEvent);
        } catch (error) {
          console.error('[SSEManager] Error in wildcard handler:', error);
        }
      });
    }
  }

  /**
   * Dispatch event to Redux store
   */
  private dispatchToStore(event: SSEEvent): void {
    if (!this.store) {
      console.warn('[SSEManager] Store not initialized. Cannot dispatch event.');
      return;
    }

    this.store.dispatch(sseEventReceived(event));
  }

  /**
   * Handle SSE errors
   */
  private handleError(error: Error): void {
    console.error('[SSEManager] SSE Error:', error);

    if (this.store) {
      this.store.dispatch(sseConnectionError(error.message));
    }
  }

  /**
   * Handle SSE connection close
   */
  private handleClose(): void {
    if (this.isIntentionalDisconnect) {
      this.isIntentionalDisconnect = false;
      return;
    }

    this.isConnected = false;

    if (this.store) {
      this.store.dispatch(sseConnectionClosed());
    }
  }

  /**
   * Reconnect with exponential backoff
   */
  public reconnect(accountReferenceId: string, maxRetries: number = 5): void {
    let retryCount = 0;

    const attemptReconnect = () => {
      if (retryCount >= maxRetries) {
        console.error('[SSEManager] Max reconnection attempts reached');
        return;
      }

      const delay = Math.min(
        PredefinedMilisecondsTypeEnum.twoSeconds ** retryCount,
        PredefinedMilisecondsTypeEnum.thirtySeconds,
      ); // Max 30s
      retryCount++;

      console.warn(`[SSEManager] Reconnecting in ${delay}ms (attempt ${retryCount}/${maxRetries})`);

      setTimeout(() => {
        if (!this.isConnected) {
          this.connect(accountReferenceId);

          // If still not connected after attempt, try again
          if (!this.isConnected) {
            attemptReconnect();
          }
        }
      }, delay);
    };

    attemptReconnect();
  }
}

export default SSEManager.getInstance();
